import { Switch } from '@progress/kendo-react-inputs';
import { Success } from 'app/components/Notifications';
import { GlobalErrorContext } from 'app/components/ReactContexts/globalErrorContext';
import { LayoutContext } from 'app/components/ReactContexts/layoutContext';
import { Select } from 'app/components/menu';
import useFlag from 'app/hooks/useFlag';
import { useGetUserInfoQuery } from 'app/hooks/user-info';
import {
  DeleteAccountConfirmationDialog,
  EditCompanyNameDialog,
  EditFirstNameDialog,
  EditLastNameDialog,
  EditPhoneNumberDialog,
} from 'app/pages/SettingsPage/Dialogs';
import { chevron_right as ChevronRight } from 'app/widgets/SVGs';
import EditIcon from 'assets/images/ic_maintenance_edit.svg';
import { useNotificationsContext } from 'context';
import * as React from 'react';
import { useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { userSettingsApi } from 'services/api/user-settings/queries';
import { FeatureName } from 'types/entities/MobileSettings';
import { UserSettings } from 'types/entities/UserSettings';
import { formatPhone } from 'utils/text/phoneMask';

export function SettingsPage() {
  const [showDeleteDialog, setShowDeleteDialog] = React.useState(false);
  const [isEditFirstNameDialogOpen, setIsEditFirstNameDialogOpen] =
    React.useState(false);
  const [isEditLastNameDialogOpen, setIsEditLastNameDialogOpen] =
    React.useState(false);
  const [showEditCompanyName, setShowEditCompanyName] = React.useState(false);
  const [showEditPhoneNumber, setShowEditPhoneNumber] = React.useState(false);

  const { data: user } = useGetUserInfoQuery();
  const { data: settings } = userSettingsApi.useGetUserSettingsQuery();

  const [updateSettings] = userSettingsApi.useUpdateUserSettingsMutation();

  const [firstName, setFirstName] = React.useState(user?.first_name ?? '');
  const [lastName, setLastName] = React.useState(user?.last_name ?? '');
  const [company, setCompany] = React.useState(user?.company_name ?? '');
  const [phoneNumber, setPhoneNumber] = React.useState(
    user?.phone_number ?? '',
  );

  const { setClassNames, removeClassNames } = React.useContext(LayoutContext);

  const { addNotification } = useNotificationsContext();
  const { addErrorModal } = React.useContext(GlobalErrorContext);
  const { t } = useTranslation();
  const isSingleSignOnFeatureEnabled = useFlag(FeatureName.SINGLE_SIGN_ON);

  // mount
  React.useEffect(() => {
    let index: number = setClassNames('layout-grey');

    return () => removeClassNames(index);
  }, []);

  useEffect(() => {
    if (user) {
      setFirstName(user?.first_name ?? '');
      setLastName(user?.last_name ?? '');
      setCompany(user?.company_name ?? '');
    }
  }, [user, setFirstName, setLastName, setCompany]);

  async function handleSettingChange(
    settingKey: keyof UserSettings,
    value: any,
  ) {
    try {
      await updateSettings({ [settingKey]: value }).unwrap();

      addNotification(
        <Success
          text={t('profile_saved')}
          additionalClass="alert-profile-saved"
        />,
      );
    } catch (error) {
      console.error('Failed to update settings:', error);
      addErrorModal();
    }
  }

  React.useEffect(() => {
    function handleClickOutside(event) {
      if (event.target.className === 'k-overlay') {
        event.stopPropagation();
        setShowDeleteDialog(false);
      }
    }

    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  });

  const measurementUnitOptions = useMemo(() => {
    return [
      { label: t('metric_unit'), value: 'METRIC' },
      { label: t('imperial_unit'), value: 'US' },
    ];
  }, [t]);

  return (
    <div className="relative">
      <h1>{t('account_and_settings')}</h1>

      <div className="form-caption">
        <span>{t('manage_preferences_and_account')}</span>
      </div>
      <div className="box-content">
        <h3>{t('general_settings')}</h3>

        <div className="settings-item">
          <div className="settings-item_label">
            <h4>{t('units')}</h4>
          </div>
          <div className="settings-item_content">
            <Select
              value={settings?.measurementUnit}
              options={measurementUnitOptions}
              onChange={value => handleSettingChange('measurementUnit', value)}
            />
          </div>
        </div>
        <div className="separator-xl"></div>
        <h3>{t('notifications')}</h3>

        <div className="settings-item">
          <div className="settings-item_label">
            <h4>{t('alerts')}</h4>
            <span className="settings-item__description">
              {t('informationAboutEquipment')}
            </span>
          </div>
          <div className="settings-item_content">
            <Switch
              checked={settings?.subscribedToAlerts}
              onChange={event =>
                handleSettingChange('subscribedToAlerts', event.target.value)
              }
              onLabel=""
              offLabel=""
            />
          </div>
        </div>

        <div className="settings-item">
          <div className="settings-item_label">
            <h4>{t('messages')}</h4>
            <span className="settings-item__description">
              {t('promotionsAndAnnouncements')}
            </span>
          </div>
          <div className="settings-item_content">
            <Switch
              checked={settings?.subscribedToMessages}
              onChange={event =>
                handleSettingChange('subscribedToMessages', event.target.value)
              }
              onLabel=""
              offLabel=""
            />
          </div>
        </div>

        <div className="separator-xl"></div>

        <h3>{t('account')}</h3>

        {isSingleSignOnFeatureEnabled && (
          <>
            <div className="settings-item">
              <div className="settings-item_label">
                {firstName ? (
                  <>
                    <span>{t('settings.fields.first_name')}</span>
                    <h4>{firstName}</h4>
                  </>
                ) : (
                  <h4>{t('settings.fields.first_name')}</h4>
                )}
              </div>
              <div
                className="settings-item_content"
                title={t('edit')}
                onClick={() => setIsEditFirstNameDialogOpen(true)}
              >
                <img src={EditIcon} alt="Edit" />
              </div>
            </div>

            <div className="settings-item">
              <div className="settings-item_label">
                {lastName ? (
                  <>
                    <span>{t('settings.fields.last_name')}</span>
                    <h4>{lastName}</h4>
                  </>
                ) : (
                  <h4>{t('settings.fields.last_name')}</h4>
                )}
              </div>
              <div
                className="settings-item_content"
                title={t('edit')}
                onClick={() => setIsEditLastNameDialogOpen(true)}
              >
                <img src={EditIcon} alt="Edit" />
              </div>
            </div>

            <div className="settings-item">
              <div className="settings-item_label">
                {company ? (
                  <>
                    <span>{t('company')}</span>
                    <h4>{company}</h4>
                  </>
                ) : (
                  <h4>{t('company')}</h4>
                )}
              </div>
              <div
                className="settings-item_content"
                title={t('edit')}
                onClick={() => setShowEditCompanyName(true)}
              >
                <img src={EditIcon} alt="Edit" />
              </div>
            </div>
            <div className="settings-item">
              <div className="settings-item_label">
                <span>{t('email')}</span>
                <h4>{user?.email}</h4>
              </div>
            </div>

            <div className="settings-item">
              <div className="settings-item_label">
                <span>{t('phone_number')}</span>
                <h4>{formatPhone(phoneNumber)}</h4>
              </div>
              <div
                className="settings-item_content"
                title={t('edit')}
                onClick={() => setShowEditPhoneNumber(true)}
              >
                <img src={EditIcon} alt="Edit" />
              </div>
            </div>
          </>
        )}

        <div
          className="settings-item clickable"
          onClick={() => setShowDeleteDialog(true)}
        >
          <div className="settings-item_label">
            <h4>{t('request_delete_account')}</h4>
          </div>
          <div className="settings-item_content">
            <ChevronRight />
          </div>
        </div>
      </div>

      {showDeleteDialog && (
        <DeleteAccountConfirmationDialog
          close={() => setShowDeleteDialog(false)}
        />
      )}

      {isEditFirstNameDialogOpen && (
        <EditFirstNameDialog
          initialValue={firstName}
          onChange={name => {
            setFirstName(name);
          }}
          close={() => setIsEditFirstNameDialogOpen(false)}
        />
      )}

      {isEditLastNameDialogOpen && (
        <EditLastNameDialog
          initialValue={lastName}
          onChange={name => {
            setLastName(name);
          }}
          close={() => setIsEditLastNameDialogOpen(false)}
        />
      )}

      {showEditCompanyName && (
        <EditCompanyNameDialog
          initialValue={company}
          onChange={company => {
            setCompany(company);
          }}
          close={() => setShowEditCompanyName(false)}
        />
      )}

      {showEditPhoneNumber && (
        <EditPhoneNumberDialog
          initialValue={phoneNumber}
          onChange={phoneNumber => {
            setPhoneNumber(phoneNumber);
          }}
          close={() => setShowEditPhoneNumber(false)}
        />
      )}
    </div>
  );
}
